import React, { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  LogOut,
  User,
  Shield,
  ChevronUp,
  DollarSign,
  Mail,
} from 'lucide-react'
import { useUserSession } from '../../context/UserSessionContext'
import { appPath } from '../../utils/appPath'

interface UserMenuProps {
  collapsed: boolean
}

export function UserMenu({ collapsed }: UserMenuProps) {
  const navigate = useNavigate()
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const {
    displayName,
    emailLine,
    initials,
    isAdmin,
    isSignedIn,
    roleLabel,
    googleUser,
    signOut,
  } = useUserSession()

  useEffect(() => {
    if (!open) return
    const close = (e: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
        setOpen(false)
      }
    }
    document.addEventListener('mousedown', close)
    return () => document.removeEventListener('mousedown', close)
  }, [open])

  const go = (path: string) => {
    setOpen(false)
    navigate(path)
  }

  const avatarInner = googleUser?.photoURL ? (
    <img
      src={googleUser.photoURL}
      alt=""
      className="w-full h-full object-cover rounded-full"
    />
  ) : (
    <span className="text-[11px] font-bold text-white">{initials}</span>
  )

  const trigger = (
    <button
      type="button"
      onClick={() => setOpen(o => !o)}
      title={collapsed ? `${displayName} — menu` : undefined}
      className={`
        flex items-center rounded-lg transition-colors group text-left
        ${collapsed ? 'justify-center p-2 w-full hover:bg-white/5' : 'gap-2.5 px-2 py-2 w-full hover:bg-white/5'}
      `}
    >
      <div
        className={`
          rounded-full bg-gradient-to-br from-blue-400 to-blue-600
          flex items-center justify-center flex-shrink-0 overflow-hidden
          ${collapsed ? 'w-8 h-8' : 'w-7 h-7'}
        `}
      >
        {avatarInner}
      </div>
      {!collapsed && (
        <>
          <div className="min-w-0 flex-1">
            <p className="text-xs font-semibold text-white truncate">
              {displayName}
            </p>
            <p className="text-[11px] text-white/55 truncate">{emailLine}</p>
          </div>
          <ChevronUp
            size={14}
            className={`text-white/70 flex-shrink-0 transition-transform ${open ? '' : 'rotate-180'}`}
          />
        </>
      )}
    </button>
  )

  const menuClass = collapsed
    ? 'absolute left-0 right-0 bottom-full mb-2 z-50'
    : 'absolute left-2 right-2 bottom-full mb-1 z-50'

  return (
    <div ref={rootRef} className={`relative ${collapsed ? 'w-full' : ''}`}>
      {trigger}
      {open && (
        <div
          className={`${menuClass} bg-slate-800 border border-slate-600/80 rounded-xl shadow-xl py-1 overflow-hidden`}
        >
          <div className="px-3 py-2 border-b border-slate-700/80">
            <p className="text-xs font-semibold text-white truncate">
              {displayName}
            </p>
            <p className="text-[11px] text-slate-400 truncate">{emailLine}</p>
            {isSignedIn && (
              <p className="text-[10px] text-slate-500 mt-1">
                Role: {roleLabel}
                {isAdmin ? ' · Elevated' : ''}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={() => go(appPath('/profile'))}
            className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/10"
          >
            <User size={16} className="text-slate-400" />
            Profile
          </button>
          {isAdmin && (
            <>
              <button
                type="button"
                onClick={() => go(appPath('/rate-config'))}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/10"
              >
                <DollarSign size={16} className="text-emerald-400/90" />
                Rate configure
              </button>
              <button
                type="button"
                onClick={() => go(appPath('/email-access'))}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/10"
              >
                <Mail size={16} className="text-sky-400/90" />
                Email access
              </button>
              <button
                type="button"
                onClick={() => go(appPath('/admin-settings'))}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-slate-200 hover:bg-white/10"
              >
                <Shield size={16} className="text-amber-400/90" />
                Admin settings
              </button>
            </>
          )}
          <div className="border-t border-slate-700/80 my-1" />
          {isSignedIn ? (
            <button
              type="button"
              onClick={() => {
                setOpen(false)
                signOut()
              }}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm text-red-300 hover:bg-red-950/40"
            >
              <LogOut size={16} />
              Sign out
            </button>
          ) : (
            <a
              href="/"
              className="flex items-center gap-2 px-3 py-2 text-sm text-blue-300 hover:bg-white/10"
              onClick={() => setOpen(false)}
            >
              <User size={16} />
              Sign in (Google)
            </a>
          )}
        </div>
      )}
    </div>
  )
}
